import { useCallback, useMemo, useState } from 'react'
import type {
  CaptionAgentContext,
  CaptionAgentMessage,
  CaptionAgentResponsePayload
} from '@/features/social/types/captionAgent'

type ConversationStatus = 'idle' | 'starting' | 'ready' | 'updating' | 'error'

export interface UseCaptionConversationOptions {
  postContext?: CaptionAgentContext
}

export interface ConversationRequestBase {
  platform?: string | null
  profileId?: string
  imageUrls: string[]
  hashtags?: string[]
}

interface AgentRequestPayload extends ConversationRequestBase {
  action: 'start' | 'refine'
  context?: CaptionAgentContext
  conversation?: CaptionAgentMessage[]
  instruction?: string
}

export function useCaptionConversation({ postContext }: UseCaptionConversationOptions) {
  const [messages, setMessages] = useState<CaptionAgentMessage[]>([])
  const [status, setStatus] = useState<ConversationStatus>('idle')
  const [error, setError] = useState<string | null>(null)

  const callAgent = useCallback(
    async (payload: AgentRequestPayload): Promise<CaptionAgentResponsePayload> => {
      const response = await fetch('/api/caption-agent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...payload,
          context: payload.context ?? postContext
        })
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error(err?.error || 'Failed to contact caption agent')
      }

      return response.json()
    },
    [postContext]
  )

  const startConversation = useCallback(
    async (request: ConversationRequestBase) => {
      if (!postContext) throw new Error('Missing AI context')
      setStatus('starting')
      setError(null)

      try {
        const result = await callAgent({
          action: 'start',
          context: postContext,
          conversation: messages,
          imageUrls: request.imageUrls,
          hashtags: request.hashtags,
          platform: request.platform ?? undefined,
          profileId: request.profileId
        } as AgentRequestPayload & ConversationRequestBase)

        setMessages(prev => [...prev, result.message])
        setStatus('ready')
        return result
      } catch (error) {
        setStatus('error')
        const message = error instanceof Error ? error.message : 'Unable to reach caption agent'
        setError(message)
        throw error
      }
    },
    [callAgent, messages, postContext]
  )

  const sendInstruction = useCallback(
    async (instruction: string, request: ConversationRequestBase) => {
      if (!instruction.trim()) throw new Error('Instruction cannot be empty')
      setStatus('updating')
      setError(null)

      const userMessage: CaptionAgentMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: instruction,
        createdAt: new Date().toISOString()
      }

      const conversation = [...messages, userMessage]

      try {
        const result = await callAgent({
          action: 'refine',
          context: postContext,
          conversation,
          instruction,
          imageUrls: request.imageUrls,
          hashtags: request.hashtags,
          platform: request.platform ?? undefined,
          profileId: request.profileId
        } as AgentRequestPayload & ConversationRequestBase)

        setMessages([...conversation, result.message])
        setStatus('ready')
        return result
      } catch (error) {
        setStatus('error')
        const message = error instanceof Error ? error.message : 'Unable to refine caption'
        setError(message)
        throw error
      }
    },
    [callAgent, messages, postContext]
  )

  const resetConversation = useCallback(() => {
    setMessages([])
    setStatus('idle')
    setError(null)
  }, [])

  const hasContext = Boolean(postContext)

  return useMemo(
    () => ({
      messages,
      status,
      error,
      hasContext,
      startConversation,
      sendInstruction,
      resetConversation
    }),
    [error, hasContext, messages, resetConversation, sendInstruction, startConversation, status]
  )
}
